import createLogger from '../../helpers/log';
import STATS_SOURCES from '../peer_connection/statsSources';
import { getBandwidth } from '../peer_connection/congestionLevel/statsUtils';
import { NETWORK_CONDITION_TO_CODE } from '../../helpers/networkConditionConstants';

const logging = createLogger('PublisherStatsSender');

const MAX_BITRATE_BITS_PER_SECOND = 10_000_000; // 10Mbps

function PublisherStatsSender(deps = {}) {
  const { streamId } = deps;
  let { socket } = deps;

  if (!socket || !streamId) {
    throw new Error('PublisherStatsSender requires socket and streamId');
  }

  const connectionsData = new Map();

  const sendStats = (peerConnectionId) => {
    const {
      peerConnection,
      networkCondition,
      networkConditionReason,
      subscriberId,
    } = connectionsData.get(peerConnectionId);
    peerConnection.getStats(STATS_SOURCES.SENDER_SIDE_STATS, (error, stats) => {
      if (error || !stats) {
        return;
      }
      const sentEstimatedBandwidthBitsPerSecond =
        Math.min(getBandwidth(stats) || -1, MAX_BITRATE_BITS_PER_SECOND);
      const sourceStreamId = peerConnection.getSourceStreamId();
      const senderStats = {
        maxBitrateBitsPerSecond: MAX_BITRATE_BITS_PER_SECOND, // 10Mbps as a placeholder
        networkCondition: NETWORK_CONDITION_TO_CODE[networkCondition],
        networkConditionReason,
        sentEstimatedBandwidthBitsPerSecond,
        sourceStreamId,
        subscriberId: sourceStreamId === 'P2P' ? subscriberId : 'MANTIS',
        timestamp: Date.now(),
        signalingNetworkScore: socket.getSignalingNetworkQualityScore(),
      };
      socket.streamChannelSendData(streamId, 'downlinkStats1', senderStats);
    });
  };

  this.addConnection = (peerConnection, peerConnectionId, subscriberId) => {
    if (!peerConnection || !peerConnectionId || !subscriberId) {
      logging.error('PublisherStatsSender requires peerConnection, peerConnectionId and subscriberId');
      return;
    }
    this.removeConnection(peerConnectionId);
    const connectionData = {
      peerConnection,
      subscriberId,
      intervalId: setInterval(() => {
        sendStats(peerConnectionId);
      }, 1000),
      networkCondition: 'good',
      networkConditionReason: 'none',
      listener: (ev) => {
        const data = connectionsData.get(peerConnectionId);
        const { networkCondition, networkConditionReason } = ev.statsContainer.mediaLink.transport;
        data.networkCondition = networkCondition;
        data.networkConditionReason = networkConditionReason;
      },
    };
    peerConnection.addEventListener('networkConditionChanged', connectionData.listener);
    connectionsData.set(peerConnectionId, connectionData);
  };

  this.removeConnection = (peerConnectionId) => {
    const connectionData = connectionsData.get(peerConnectionId);
    if (!connectionData) {
      return;
    }
    const { peerConnection, intervalId, listener } = connectionData;
    clearInterval(intervalId);
    peerConnection.removeEventListener('networkConditionChanged', listener);
    connectionsData.delete(peerConnectionId);
  };

  this.setSocket = (newSocket) => {
    if (!newSocket) {
      logging.error('PublisherStatsSender requires a valid socket');
      return;
    }
    socket = newSocket;
  };

  this.destroy = () => {
    connectionsData.forEach((connectionData, peerConnectionId) => {
      this.removeConnection(peerConnectionId);
    });
  };
}

export default PublisherStatsSender;
